import React, { forwardRef } from 'react';
import classnames from 'classnames';

// prefix and classNamePrefix is used to create className only

interface WrapperProps {
  children: string | JSX.Element | JSX.Element[];
  status: string;
  size: string;
  disabled: boolean;
  minWidth?: boolean;
  maxWidth?: boolean;
}

export interface InputProps {
  id?: string;
  outline?: boolean | string;
  status?:
    | 'primary'
    | 'secondary'
    | 'danger'
    | 'warning'
    | 'success'
    | 'info'
    | 'dark'
    | 'default';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';

  placeholder?: string;
  type?: string;

  disabled?: boolean;
  className?: string;

  children?: string | JSX.Element | JSX.Element[];

  prefix?: string | JSX.Element | JSX.Element[];
  suffix?: string | JSX.Element | JSX.Element[];

  addonBefore?: string | JSX.Element | JSX.Element[];
  addonAfter?: string | JSX.Element | JSX.Element[];

  name: string;
  controlling?: boolean;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  readOnly?: boolean;
  defaultValue?: string;
  minWidth?: boolean;
  maxWidth?: boolean;
  select?: boolean;
  selectOption?: { label: string; value: string }[];
  onSelectionChange?: (e) => void;
  selectValue?: string;
  selectDefaultValue?: string;
  autoFocus?: boolean;
  onFocus?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  testId?: string;
}

const Wrapper = ({
  children,
  status,
  size,
  disabled,
  minWidth,
  maxWidth,
}: WrapperProps) => {
  return (
    <div
      className={classnames({
        'input-group-wrap': true,
        [`input-group-wrap-${status}`]: status,
        [`input-group-wrap-${size}`]: size,
        'input-group-wrap-disabled': disabled,
        'input-min-width': minWidth,
        'input-max-width': maxWidth,
      })}
    >
      {children}
    </div>
  );
};

// eslint-disable-next-line react/display-name
const InputComponent = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      id,
      name,
      className = '',
      placeholder = '',
      type = 'text',
      prefix,
      suffix,
      addonBefore,
      addonAfter,
      disabled,
      size,
      controlling,
      value,
      onChange,
      readOnly,
      defaultValue,
      minWidth,
      maxWidth,
      select,
      selectOption,
      selectDefaultValue,
      selectValue,
      onSelectionChange,
      autoFocus,
      onFocus,
      testId,
      ...props
    }: InputProps,
    ref,
  ) => {
    return (
      <div
        className={classnames('input-wrap', {
          [`input-wrap-${size}`]: size,
          'input-wrap-disabled': disabled,
          'input-min-width': minWidth,
          'input-max-width': maxWidth,
        })}
      >
        {addonBefore ? (
          <div className="input-group_add-before">{addonBefore}</div>
        ) : null}
        {prefix ? (
          <div className="input-group_prefix d-flex">{prefix}</div>
        ) : null}
        {select ? (
          <select
            className="input-wrap-select"
            defaultValue={selectDefaultValue}
            value={selectValue}
            onChange={(e) => onSelectionChange(e)}
          >
            {selectOption?.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        ) : null}
        <input
          ref={ref}
          {...props}
          id={id}
          name={name}
          className={classnames('input', className)}
          placeholder={placeholder}
          type={type}
          disabled={disabled}
          readOnly={readOnly}
          autoFocus={autoFocus}
          {...(typeof onFocus === 'function' && { onFocus })}
          {...(controlling && { value, onChange })}
          {...(!controlling && { defaultValue })}
          data-testid={testId}
        />
        {suffix ? <div className="input-group_suffix">{suffix}</div> : null}
        {addonAfter ? (
          <div className="input-group_add-after">{addonAfter}</div>
        ) : null}
      </div>
    );
  },
);

// eslint-disable-next-line react/display-name
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      id,
      outline = true,
      size = 'md',
      status,
      className = '',
      placeholder = '',
      disabled = false,
      type = 'text',
      prefix,
      suffix,
      addonBefore,
      addonAfter,
      name,
      controlling,
      value,
      onChange,
      readOnly,
      defaultValue,
      minWidth,
      maxWidth,
      autoFocus,
      onFocus,
      testId,
      ...props
    }: InputProps,
    ref,
  ) => {
    return outline ? (
      <Wrapper
        status={status}
        size={size}
        disabled={disabled}
        minWidth={minWidth}
        maxWidth={maxWidth}
      >
        <InputComponent
          ref={ref}
          {...props}
          id={id}
          outline={outline?.toString()}
          className={className}
          placeholder={placeholder}
          disabled={disabled}
          type={type}
          prefix={prefix}
          suffix={suffix}
          addonBefore={addonBefore}
          addonAfter={addonAfter}
          size={size}
          name={name}
          controlling={controlling}
          value={value}
          onChange={onChange}
          readOnly={readOnly}
          defaultValue={defaultValue}
          autoFocus={autoFocus}
          onFocus={onFocus}
          testId={testId}
        />
      </Wrapper>
    ) : (
      <InputComponent
        ref={ref}
        {...props}
        id={id}
        outline={outline}
        className={className}
        placeholder={placeholder}
        disabled={disabled}
        type={type}
        prefix={prefix}
        suffix={suffix}
        addonBefore={addonBefore}
        addonAfter={addonAfter}
        size={size}
        name={name}
        controlling={controlling}
        value={value}
        onChange={onChange}
        readOnly={readOnly}
        defaultValue={defaultValue}
        minWidth={minWidth}
        maxWidth={maxWidth}
        autoFocus={autoFocus}
        onFocus={onFocus}
        testId={testId}
      />
    );
  },
);
